import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from 'components';
import {
  Account,
  StudyCreationDto,
  useCreateStudy as useCreateStudyQuery,
  useCurrentOrganizationId,
  useMe,
} from 'queries';
import { useBoolModalState } from '@app/core';

interface Options<T> {
  createDto: (
    values: T,
  ) => Omit<StudyCreationDto, 'organizationId'> | undefined;
}

export const useCreateStudy = <T = void>({ createDto }: Options<T>) => {
  const navigate = useNavigate();
  const toast = useToast();

  const { data: me } = useMe(true);
  const organizationId = useCurrentOrganizationId(true);
  const [createStudyDto, setCreateStudyDto] = useState<
    Partial<StudyCreationDto>
  >({});
  const [loginIsOpened, openLogin, closeLogin] = useBoolModalState(false);

  const { mutateAsync: createStudy } = useCreateStudyQuery();

  const onLoginSuccess = useCallback(
    async (me: Account, _dto?: Partial<StudyCreationDto>) => {
      const dto = _dto ? _dto : createStudyDto;
      const updatedOrganizationId = organizationId
        ? organizationId
        : me.organizations[0]?.organizationId || undefined;

      if (!dto) {
        toast.invalidForm();
        return;
      }
      try {
        const { id } = await createStudy({
          ...dto,
          organizationId: updatedOrganizationId,
        });
        toast.saved();
        navigate(`/sessions/edit/${id}/settings`);
      } catch (e) {
        toast.exception(e);
      }
    },
    [navigate, createStudy, toast, createStudyDto, organizationId], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onSubmit = useCallback(
    async (values: T) => {
      const dto = createDto(values);
      if (!dto) {
        return;
      }
      if (!me || !me.organizations?.length) {
        setCreateStudyDto(dto);
        return openLogin();
      }
      onLoginSuccess(me, dto);
    },
    [organizationId, openLogin, onLoginSuccess, me, createDto], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    onSubmit,
    loginIsOpened,
    closeLogin,
    onLoginSuccess,
  };
};
